import { useMemo, useState } from "react";
import { Topbar } from "../ui/Topbar";
import { usePortfolio } from "../features/portfolio/usePortfolio";
import { useFxRates } from "../features/portfolio/useFxRates";
import { useClosedPositions } from "../features/portfolio/useClosedPositions";
import { useSchedulesByPosition } from "../features/dashboard/useSchedulesByPosition";
import { CalendarGrid } from "../features/calendar/CalendarGrid";
import {
  buildEventsByDate,
  monthEvents,
  monthLabel,
  monthTotal,
} from "../features/calendar/calendarData";
import { fmtDate, fmtRub } from "../lib/format";
import { useScrollRestore } from "../lib/useScrollRestore";

export default function CalendarPage() {
  const today = useMemo(() => new Date(), []);
  const todayIso = useMemo(
    () =>
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`,
    [today],
  );

  const [year, setYear] = useState(today.getFullYear());
  const [month0, setMonth0] = useState(today.getMonth());

  const portfolio = usePortfolio();
  const closed = useClosedPositions();
  const positions = useMemo(
    () => [...(portfolio.data?.positions ?? []), ...(closed.data ?? [])],
    [portfolio.data, closed.data],
  );
  const secids = useMemo(() => positions.map((p) => p.position.secid), [positions]);
  const { byId: schedulesById } = useSchedulesByPosition(secids);
  const fx = useFxRates();
  const fxRates = fx.data ?? {};

  const eventsByDate = useMemo(
    () => buildEventsByDate(positions, schedulesById, fxRates),
    [positions, schedulesById, fxRates],
  );
  const total = useMemo(
    () => monthTotal(eventsByDate, year, month0),
    [eventsByDate, year, month0],
  );
  const events = useMemo(
    () => monthEvents(eventsByDate, year, month0),
    [eventsByDate, year, month0],
  );

  const scrollRef = useScrollRestore<HTMLDivElement>("calendar");

  const goPrev = () => {
    if (month0 === 0) {
      setMonth0(11);
      setYear((y) => y - 1);
    } else {
      setMonth0((m) => m - 1);
    }
  };
  const goNext = () => {
    if (month0 === 11) {
      setMonth0(0);
      setYear((y) => y + 1);
    } else {
      setMonth0((m) => m + 1);
    }
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonth0(today.getMonth());
  };

  const isCurrentMonth =
    year === today.getFullYear() && month0 === today.getMonth();

  return (
    <>
      <Topbar title="Календарь купонов" />

      <div ref={scrollRef} className="page-scroll">
        {portfolio.isPending && <div className="banner">загружаю портфель…</div>}

        {portfolio.isError && (
          <div className="banner error">
            ошибка: {String(portfolio.error)}
          </div>
        )}

        {portfolio.data && positions.length === 0 && (
          <div className="banner">
            нет открытых позиций — добавь первую в портфеле
          </div>
        )}

        {portfolio.data && positions.length > 0 && (
          <>
            <div className="cal-nav">
              <button className="btn sm" onClick={goPrev} aria-label="prev month">
                ‹
              </button>
              <span className="serif cal-nav-label">
                {monthLabel(year, month0)}
              </span>
              <button className="btn sm" onClick={goNext} aria-label="next month">
                ›
              </button>
              {!isCurrentMonth && (
                <button className="btn sm" onClick={goToday}>
                  сегодня
                </button>
              )}
            </div>

            <div className="cal-month-banner">
              <span className="cal-month-banner-label">в этом месяце</span>
              <span className="cal-month-banner-value mono">
                {total > 0 ? `+${fmtRub(total)}` : fmtRub(total)}
              </span>
            </div>

            <CalendarGrid
              year={year}
              month0={month0}
              events={eventsByDate}
              todayIso={todayIso}
            />

            <div className="legend" style={{ marginTop: 12 }}>
              <span>
                <span className="sw cev-recv" />
                зачислено
              </span>
              <span>
                <span className="sw cev-coupon" />
                ожидается
              </span>
              <span style={{ marginLeft: "auto" }} className="soft">
                наведи на чип — детали выплаты
              </span>
            </div>

            {events.length > 0 && (
              <div className="cal-payments">
                <div className="cal-payments-head">
                  выплаты · {events.length}
                </div>
                {events.map((e, i) => (
                  <div
                    key={`${e.iso}-${e.secid}-${i}`}
                    className={`cal-payment${e.received === true ? " recv" : ""}`}
                  >
                    <div className="cal-payment-main">
                      <div className="cal-payment-name">{e.name}</div>
                      <div className="cal-payment-meta mono">
                        {fmtDate(e.iso)} · {e.secid}
                        {e.type === "amort" && " · погашение"}
                        {e.received === true && " · зачислено"}
                        {e.projected && " · проекция"}
                      </div>
                    </div>
                    <div
                      className={`cal-payment-amount mono${e.type === "amort" ? " accent" : " ok"}${e.projected ? " soft" : ""}`}
                    >
                      +{fmtRub(e.amount, 0)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
